import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import useItemStore from '@store/useItemStore'
import { apiClient } from '@api'
import { Modal, Button, SearchBar } from '@components/common'
import { Building2, Check, AlertCircle, CheckCircle2, Users } from 'lucide-react'

/**
 * ItemAssign Component
 *
 * Modal for assigning items to companies.
 */
const ItemAssign = ({ onClose }) => {
  const {
    selectedItem,
    assignments,
    assignmentsLoading,
    fetchAssignments,
    assignToCompanies,
    unassignFromCompany,
  } = useItemStore()

  const [companies, setCompanies] = useState([])
  const [companiesLoading, setCompaniesLoading] = useState(true)
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([])
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)

  // Fetch companies and assignments on mount
  useEffect(() => {
    if (selectedItem?.id) {
      fetchAssignments(selectedItem.id)
      loadCompanies()
    }
  }, [selectedItem?.id])

  // Initialize selected companies from assignments
  useEffect(() => {
    if (assignments.length > 0) {
      setSelectedCompanyIds(assignments.map((a) => a.company_id))
    }
  }, [assignments])

  /**
   * Load all active companies
   */
  const loadCompanies = async () => {
    try {
      setCompaniesLoading(true)
      const response = await apiClient.get('/companies?status=active&limit=1000')
      setCompanies(response.data?.companies || response.data || [])
    } catch (error) {
      console.error('Failed to load companies:', error)
    } finally {
      setCompaniesLoading(false)
    }
  }

  /**
   * Toggle company selection
   */
  const toggleCompany = (companyId) => {
    setSelectedCompanyIds((prev) => {
      if (prev.includes(companyId)) {
        return prev.filter((id) => id !== companyId)
      }
      return [...prev, companyId]
    })
  }

  /**
   * Toggle all companies
   */
  const toggleAll = () => {
    if (selectedCompanyIds.length === filteredCompanies.length) {
      setSelectedCompanyIds([])
    } else {
      setSelectedCompanyIds(filteredCompanies.map((c) => c.id))
    }
  }

  /**
   * Check if company has update available
   */
  const getAssignmentInfo = (companyId) => {
    return assignments.find((a) => a.company_id === companyId)
  }

  /**
   * Filter companies by search
   */
  const filteredCompanies = companies.filter((company) =>
    company.name.toLowerCase().includes(search.toLowerCase())
  )

  /**
   * Handle save
   */
  const handleSave = async () => {
    if (!selectedItem?.id) return

    setSaving(true)

    try {
      // Get newly selected companies (not already assigned)
      const currentlyAssigned = assignments.map((a) => a.company_id)
      const newlySelected = selectedCompanyIds.filter(
        (id) => !currentlyAssigned.includes(id)
      )
      const toUnassign = currentlyAssigned.filter(
        (id) => !selectedCompanyIds.includes(id)
      )

      // Assign new companies
      if (newlySelected.length > 0) {
        await assignToCompanies(selectedItem.id, newlySelected, selectedItem.current_version)
      }

      // Unassign removed companies
      for (const companyId of toUnassign) {
        await unassignFromCompany(selectedItem.id, companyId)
      }

      onClose()
    } catch (error) {
      console.error('Failed to save assignments:', error)
    } finally {
      setSaving(false)
    }
  }

  if (!selectedItem) return null

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Assign: ${selectedItem.title}`}
      size="medium"
      closeOnBackdrop={false}
    >
      <div className="item-assign">
        <p className="item-assign__description">
          Select companies to assign this item to. They will receive version <strong>{selectedItem.current_version}</strong>.
        </p>

        <div className="item-assign__search">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search companies..."
          />
        </div>

        {companiesLoading || assignmentsLoading ? (
          <div className="loading-state">Loading companies...</div>
        ) : filteredCompanies.length === 0 ? (
          <div className="empty-state">No companies found.</div>
        ) : (
          <div className="company-list">
            {/* Select All */}
            <label htmlFor="select-all" className="select-all-row">
              <div className="select-all-row__checkbox">
                <input
                  type="checkbox"
                  checked={selectedCompanyIds.length === filteredCompanies.length && filteredCompanies.length > 0}
                  onChange={toggleAll}
                  id="select-all"
                />
                <span className="custom-checkbox">
                  {selectedCompanyIds.length === filteredCompanies.length && filteredCompanies.length > 0 && (
                    <Check size={12} />
                  )}
                </span>
              </div>
              <span className="select-all-row__text">
                Select All ({filteredCompanies.length} companies)
              </span>
            </label>

            {/* Company List */}
            <div className="company-list__items">
              {filteredCompanies.map((company) => {
                const assignmentInfo = getAssignmentInfo(company.id)
                const isSelected = selectedCompanyIds.includes(company.id)
                const hasUpdate = assignmentInfo?.has_update

                return (
                  <label
                    key={company.id}
                    htmlFor={`company-${company.id}`}
                    className={`company-item ${isSelected ? 'company-item--selected' : ''} ${assignmentInfo ? 'company-item--assigned' : ''}`}
                  >
                    <div className="company-item__checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleCompany(company.id)}
                        id={`company-${company.id}`}
                      />
                      <span className="custom-checkbox">
                        {isSelected && <Check size={12} />}
                      </span>
                    </div>
                    <div className="company-item__icon">
                      <Building2 size={16} />
                    </div>
                    <div className="company-item__info">
                      <div className="company-item__name">{company.name}</div>
                      {assignmentInfo && (
                        <div className="company-item__meta">
                          <span className="company-item__version-badge">
                            Assigned v{assignmentInfo.item_version}
                          </span>
                          {hasUpdate && (
                            <span className="company-item__update-badge">
                              <AlertCircle size={10} />
                              Update available
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {assignmentInfo && (
                      <div className="company-item__status">
                        <CheckCircle2 size={18} />
                      </div>
                    )}
                  </label>
                )
              })}
            </div>
          </div>
        )}

        <div className="item-assign__footer">
          <div className="item-assign__summary">
            <Users size={16} />
            <span><strong>{selectedCompanyIds.length}</strong> {selectedCompanyIds.length === 1 ? 'company' : 'companies'} selected</span>
          </div>

          <div className="item-assign__actions">
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Assignments'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

ItemAssign.propTypes = {
  onClose: PropTypes.func.isRequired,
}

export default ItemAssign
